// Botón `?` + modal de ayuda de Modelia (`showTt()` del mock). El texto no es el del mock: describe
// el motor de scoring que existe hoy, no el connector del proveedor que el mock dibujó.
import { useState, type ReactNode } from 'react';
import { Eye, LayoutList, Scale, SearchX, type LucideIcon } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

function HelpSection({
    icon: Icon,
    title,
    children,
}: {
    icon: LucideIcon;
    title: string;
    children: ReactNode;
}) {
    return (
        <section className="flex gap-2.5">
            <span className="mt-0.5 flex size-6 flex-none items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground">
                <Icon className="size-3.5" aria-hidden />
            </span>
            <div className="min-w-0 space-y-1">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.04em] text-foreground">
                    {title}
                </h3>
                <div className="space-y-1.5 text-[11.5px] leading-relaxed text-muted-foreground">
                    {children}
                </div>
            </div>
        </section>
    );
}

export function ModeliaHelpDialog() {
    const [open, setOpen] = useState(false);

    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-6 w-6 flex-none rounded-full text-[11px] font-bold text-muted-foreground"
                aria-label="¿Qué es esta pantalla?"
                title="¿Qué es esta pantalla?"
                onClick={() => {
                    setOpen(true);
                }}
            >
                ?
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="text-base">
                            Modelia · scoring de crédito
                        </DialogTitle>
                        <DialogDescription className="text-[11.5px]">
                            Qué evaluó el motor de crédito y con qué parámetros lo hizo.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
                        <HelpSection icon={Eye} title="Qué es esta pantalla">
                            <p>
                                Es la vista de las evaluaciones de crédito que el motor de scoring
                                guardó. Cada evaluación es la foto de una decisión: qué score sacó
                                el solicitante, en qué nivel cayó y si se le aprobó crédito.
                            </p>
                        </HelpSection>

                        <HelpSection icon={Scale} title="SCI y Nivel son dos cosas distintas">
                            <ul className="list-disc space-y-1 pl-4">
                                <li>
                                    <strong className="font-semibold text-foreground">SCI</strong>{' '}
                                    es el score, en escala 0 a 100.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">Nivel</strong>{' '}
                                    —Oro, Plata, Bronce o No elegible— se deriva del SCI aplicando
                                    los cortes que muestra el botón Modelo.
                                </li>
                            </ul>
                            <p>
                                No es el nivel de cuenta de la wallet: eso es otra escalera, de otro
                                producto, y esta pantalla no habla de ella.
                            </p>
                        </HelpSection>

                        <HelpSection icon={LayoutList} title="Cómo está organizada">
                            <p>
                                Es una sola pantalla: filtros, lista y ficha con la oferta y el
                                desglose por capas.
                            </p>
                            <ul className="list-disc space-y-1 pl-4">
                                <li>
                                    La casilla{' '}
                                    <strong className="font-semibold text-foreground">
                                        Solo contradictorias
                                    </strong>{' '}
                                    del panel de filtros deja la lista con las que llegaron
                                    aprobadas y un SCI o un nivel que no las sostiene. En la lista
                                    van marcadas como INCONSISTENTE.
                                </li>
                                <li>
                                    La{' '}
                                    <strong className="font-semibold text-foreground">
                                        búsqueda
                                    </strong>{' '}
                                    del mismo panel acepta el nombre del acreditado y los ids de la
                                    evaluación y del acreditado. El teléfono todavía no: el listado
                                    del motor no lo devuelve.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Modelo
                                    </strong>
                                    : el botón de aquí arriba abre los parámetros con los que el
                                    motor calcula el SCI, en solo lectura.
                                </li>
                            </ul>
                        </HelpSection>

                        <HelpSection icon={SearchX} title="Qué NO vas a encontrar aquí">
                            <p>
                                La lista baja por páginas. Mientras el botón{' '}
                                <strong className="font-semibold text-foreground">
                                    Cargar más
                                </strong>{' '}
                                siga ahí quedan evaluaciones sin bajar, y{' '}
                                <strong className="font-semibold text-foreground">
                                    los filtros, la búsqueda y los conteos miran solo lo cargado
                                </strong>
                                . El rótulo debajo de la lista dice sobre cuántas está contando.
                            </p>
                            <p>
                                Tampoco hay latencias, error rate ni el request y el response crudos
                                del proveedor: eso vive en el audit log del connector y ningún
                                endpoint de backoffice lo publica. Nada de eso se rellena con ceros
                                aquí: lo que no tiene fuente no se pinta.
                            </p>
                        </HelpSection>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
